import { useAddress, useContract } from '@thirdweb-dev/react';
import { NATIVE_TOKEN_ADDRESS } from '@thirdweb-dev/sdk';
import { add } from 'date-fns';
import { useRouter } from 'next/router';
import { useEffect } from 'react';
import { useAsync, useAsyncFn } from 'react-use';

import {
  bigNumberToDecimal,
  numberToBigNumber,
  parseUnits
} from '@app/helpers/format';
import { getSwapSettings } from '@app/hooks/swap/useSwapSettings';
import { Pair } from '@app/types/pool';

// eslint-disable-next-line no-restricted-imports
import routerAbi from '../../../abis/router.json';
// eslint-disable-next-line no-restricted-imports
import pairAbi from '../../../abis/pair.json';
import { useConfig } from '@app/config';

export function useWithdrawLiquidity(pair: Pair | undefined, amount: string) {
  const { query } = useRouter();
  const address = useAddress();
  const config = useConfig();
  const { contract } = useContract(config?.CONTRACTS.ROUTER, routerAbi);
  const { contract: pairContract } = useContract(
    pair?.id ?? (query.id as string),
    pairAbi
  );

  const { value: pairDecimals } = useAsync(async () => {
    if (!pairContract) {
      return;
    }

    return pairContract.call('decimals');
  }, [pairContract]);

  const [{ loading: approvingWithdraw }, approveWithdraw] =
    useAsyncFn(async () => {
      if (
        !pairContract ||
        !config?.CONTRACTS.ROUTER ||
        !amount ||
        !pairDecimals
      ) {
        return;
      }

      const _amount = numberToBigNumber(amount, pairDecimals);

      const res = await pairContract.call('approve', [
        config?.CONTRACTS.ROUTER,
        _amount
      ]);

      await getAllowance();

      return res;
    }, [amount, pairContract, pairDecimals, config]);

  const [{ value: allowance, loading: fetchingAllowance }, getAllowance] =
    useAsyncFn(async () => {
      if (
        !pairContract ||
        !address ||
        !config?.CONTRACTS.ROUTER ||
        !amount ||
        !pairDecimals
      ) {
        return;
      }

      try {
        const res = await pairContract.call('allowance', [
          address,
          config?.CONTRACTS.ROUTER
        ]);

        return bigNumberToDecimal(res, pairDecimals);
      } catch (e) {
        // do nothing
        console.error(e);
      }
    }, [pairContract, address, amount, pairDecimals, config]);

  const [{ loading: withdrawingLiquidity, error }, withdrawLiquidity] =
    useAsyncFn(async () => {
      if (!pair || !amount || !contract || !pairDecimals) {
        return;
      }

      const settings = await getSwapSettings();

      const token0 = pair.token0;
      const token1 = pair.token1;

      if (token0.id === NATIVE_TOKEN_ADDRESS) {
        // eth => erc20
        return contract.call(
          'removeLiquidityETHSupportingFeeOnTransferTokens',
          [
            token1.id,
            parseUnits(amount, +pairDecimals),
            0,
            0,
            address,
            add(new Date(), { minutes: settings.deadline }).getTime()
          ]
        );
      } else if (token1.id === NATIVE_TOKEN_ADDRESS) {
        // erc20 => eth
        return contract.call(
          'removeLiquidityETHSupportingFeeOnTransferTokens',
          [
            token0.id,
            parseUnits(amount, +pairDecimals),
            0,
            0,
            address,
            add(new Date(), { minutes: settings.deadline }).getTime()
          ]
        );
      } else {
        const formatted = parseUnits(amount, +pairDecimals);

        return contract.call('removeLiquidity', [
          token0.id,
          token1.id,
          formatted,
          0,
          0,
          address,
          add(new Date(), { minutes: settings.deadline }).getTime()
        ]);
      }
    }, [
      contract,
      amount,
      pair,
      allowance,
      approveWithdraw,
      pairDecimals,
      address
    ]);

  useEffect(() => {
    getAllowance();
  }, [getAllowance]);

  return {
    error,
    allowance,
    getAllowance,
    withdrawingLiquidity,
    fetchingAllowance,
    approvingWithdraw,
    withdrawLiquidity,
    approveWithdraw,
    pairDecimals
  };
}
